import { type GlobalErrorHandler } from '@amzn/vinyl-amzn'
import type { Disposable, Disposer, LogTarget } from '@amzn/vinyl-util'
import {
    createDisposer,
    ErrorOrigin,
    globalRef,
    isSilentError,
    submitErrorReport,
} from '@amzn/vinyl-util'
import { windowEvents } from '@/util/interaction'

export function initializeErrorHandling() {
    void globalErrorHandlerRef.value
}

export const globalErrorHandlingTarget: LogTarget = {
    logPrefix: '<globalError>',
}

/**
 * Adds uncaught error and promise rejection handling.
 */
export class GlobalErrorHandlerImpl implements GlobalErrorHandler, Disposable {
    private readonly errorHandler = (event: ErrorEvent) => {
        const error = event.error
        if (!(error instanceof Error)) return
        if (this.reportError(error)) {
            event.preventDefault()
        }
    }

    private readonly unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
        const error = event.reason
        if (!(error instanceof Error)) return
        if (this.reportError(error)) {
            event.preventDefault()
        }
    }

    private disposer: Disposer

    constructor() {
        this.disposer = createDisposer()
        const add = this.disposer.add

        /**
         * Global uncaught exception handler.
         */
        add(windowEvents.on('error', this.errorHandler))

        /**
         * Global unhandled rejection handler.
         */
        add(windowEvents.on('unhandledrejection', this.unhandledRejectionHandler))
    }

    addRange(): boolean {
        // Ignore range, report from any location
        return true
    }

    /**
     * Submits an error report if the error is not silent.
     *
     * @param error
     */
    private reportError(error: Error): boolean {
        if (isSilentError(error)) return false
        submitErrorReport({
            target: globalErrorHandlingTarget,
            error,
            origin: ErrorOrigin.UNCAUGHT,
        })
        return true
    }

    dispose() {
        this.disposer.dispose()
    }
}

export const globalErrorHandlerRef = globalRef<GlobalErrorHandler | undefined>(
    () => new GlobalErrorHandlerImpl()
)
